/**
 * Tenant profile store — THE frontend source of truth for the tenant's
 * currency / tax / country / locale profile (GET /api/v1/settings subset).
 *
 * Resolution contract (mirrors the backend):
 *  1. A record's own stored value always wins (callers' concern).
 *  2. Anything unset resolves from this profile.
 *  3. Fallback chain: hydrated profile → persisted (last-login) profile →
 *     UAE last-resort literal, with a one-time console.warn. Existing-tenant
 *     behavior is unchanged because the last-resort literals equal today's
 *     hardcoded values.
 *
 * Persisted to localStorage (like auth-store) so a page refresh renders with
 * the last-known profile instead of flashing UAE defaults for non-UAE tenants.
 */

import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'
import { authFetch } from '@/lib/api'

interface ApiResponse<T> {
  success: boolean
  message: string
  data: T
  timestamp?: string
}

/** Subset of users-service GET /api/v1/settings this store reads. */
interface TenantSettingsResponse {
  companyName: string
  defaultCountry?: string
  defaultCountryName?: string
  defaultTimezone?: string
  trn?: string | null
  taxLabel?: string
  taxIdLabel?: string
  defaultTaxRatePercent?: number | string
  financeSettings?: { defaultCurrency?: string } | null
}

export interface TenantProfile {
  companyName: string
  baseCurrency: string | null
  taxLabel: string
  taxIdLabel: string
  defaultTaxRatePercent: number | null
  country: string
  countryName: string
  timezone: string
  taxIdValue: string | null
  locale: string
}

/**
 * Country → BCP-47 locale, as DATA (extensible per country without code
 * branches). Unknown countries fall back to the current app-wide default.
 */
const COUNTRY_LOCALE: Record<string, string> = {
  AE: 'en-AE',
  IN: 'en-IN',
  US: 'en-US',
  GB: 'en-GB',
}
const DEFAULT_LOCALE = 'en-AE'

interface TenantState {
  profile: TenantProfile | null
  isHydrated: boolean
  hydrate: (token: string) => Promise<void>
  reset: () => void
}

function toProfile(data: TenantSettingsResponse): TenantProfile {
  const country = data.defaultCountry || 'AE'
  const rate = data.defaultTaxRatePercent
  return {
    companyName: data.companyName,
    baseCurrency: data.financeSettings?.defaultCurrency || null,
    taxLabel: data.taxLabel || 'VAT',
    taxIdLabel: data.taxIdLabel || 'TRN',
    defaultTaxRatePercent:
      rate === undefined || rate === null ? null : Number(rate),
    country,
    countryName: data.defaultCountryName || '',
    timezone: data.defaultTimezone || 'Asia/Dubai',
    taxIdValue: data.trn || null,
    locale: COUNTRY_LOCALE[country] ?? DEFAULT_LOCALE,
  }
}

/**
 * In-flight hydration guard — on a fresh login both authService.login() and
 * ProtectedRoute fire hydrate() before the first resolves, producing two
 * concurrent GET /api/v1/settings calls. Module-level promise reuse dedupes
 * them into a single fetch (per token).
 */
let inFlightHydration: { token: string; promise: Promise<void> } | null = null

export const useTenantStore = create<TenantState>()(
  persist(
    (set) => ({
      profile: null,
      isHydrated: false,

      hydrate: (token: string) => {
        if (inFlightHydration && inFlightHydration.token === token) {
          return inFlightHydration.promise
        }
        const promise = (async () => {
          try {
            const res = await authFetch<ApiResponse<TenantSettingsResponse>>(
              '/api/v1/settings',
              token
            )
            if (res?.data) {
              set({ profile: toProfile(res.data), isHydrated: true })
            }
          } catch (err) {
            // Persisted last-login profile (if any) keeps serving; hooks fall
            // back to UAE literals otherwise. Never throw into the login flow.
            console.warn('[tenant] profile hydration failed', err)
          } finally {
            inFlightHydration = null
          }
        })()
        inFlightHydration = { token, promise }
        return promise
      },

      reset: () => set({ profile: null, isHydrated: false }),
    }),
    {
      name: 'freighai-tenant',
      version: 1,
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({ profile: state.profile }),
    }
  )
)

/* Non-hook getters for pure util modules (formatters etc.) that cannot call
 * hooks — mirrors getAccessToken in auth-store. The UAE literals are the
 * last resort of the D-chain and equal today's hardcoded behavior. */
const warned = new Set<string>()
function fallback<T>(field: string, value: T | null | undefined, last: T): T {
  if (value !== null && value !== undefined && value !== '') return value as T
  if (!warned.has(field)) {
    warned.add(field)
    console.warn(
      `[tenant] falling back to platform default for ${field} — profile not hydrated`
    )
  }
  return last
}

export const getTenantCurrency = (): string =>
  fallback('baseCurrency', useTenantStore.getState().profile?.baseCurrency, 'AED')
export const getTaxLabel = (): string =>
  fallback('taxLabel', useTenantStore.getState().profile?.taxLabel, 'VAT')
export const getTaxIdLabel = (): string =>
  fallback('taxIdLabel', useTenantStore.getState().profile?.taxIdLabel, 'TRN')
export const getTenantCountry = (): string =>
  fallback('country', useTenantStore.getState().profile?.country, 'AE')
export const getTenantLocale = (): string =>
  fallback('locale', useTenantStore.getState().profile?.locale, DEFAULT_LOCALE)
export const getTenantTimezone = (): string =>
  fallback('timezone', useTenantStore.getState().profile?.timezone, 'Asia/Dubai')
